/**
 * WHETHER ANYTHING ON SCREEN WOULD BE LOST BY RELOADING.
 *
 * WHY THIS EXISTS AT ALL. #268 puts an "Update" button in front of people, and
 * updating means reloading — which throws away anything typed and not saved. A
 * notice that loses somebody's paragraph is worse than no notice, so the button
 * has to be able to ask the question before it acts.
 *
 * NOTHING ELSE IN THE APP COULD ANSWER IT. Edits live in component state, so
 * "is anything unsaved" was not a question anywhere could answer — which is
 * also why nothing warns before an ordinary reload today. This is the smallest
 * thing that can answer it: a count, raised while a component holds unsaved
 * text and lowered when it stops.
 *
 * A COUNT AND NOT A FLAG, because two things can be open at once — a card being
 * edited with a half-written comment under it — and a flag would be turned off
 * by whichever finished first.
 *
 * IT SAYS NOTHING ABOUT WHAT IS UNSAVED, deliberately. The count is all the
 * button needs, and the text people type is the one thing the trail refuses to
 * keep (see src/trail.ts). This holds to the same line.
 */
let holding = 0;
const watchers = new Set<(n: number) => void>();

const tell = () => watchers.forEach((w) => w(holding));

/**
 * Register that something unsaved is being held. Returns the release, which is
 * safe to call twice — a component unmounting after it has already let go must
 * not drive the count below zero and hide somebody else's unsaved work.
 */
export function holdingUnsaved(): () => void {
  holding += 1;
  tell();
  let released = false;
  return () => {
    if (released) return;
    released = true;
    holding -= 1;
    tell();
  };
}

export const somethingIsUnsaved = (): boolean => holding > 0;

/** For anything that has to redraw when the answer changes. */
export function watchUnsaved(fn: (n: number) => void): () => void {
  watchers.add(fn);
  return () => void watchers.delete(fn);
}

/** For tests, and for nothing else. */
export function forgetUnsaved(): void {
  holding = 0;
  watchers.clear();
}
